import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import ArtworkCard from "../components/ArtworkCard";
import Seo, { SERIES_SEO } from "../lib/seo";

const SERIES = [
  { value: "all", label: "All" },
  { value: "genesis", label: "Genesis" },
  { value: "explorations", label: "Explorations" },
  { value: "objects", label: "Objects" },
  { value: "field", label: "Field" },
];

export default function Archive() {
  const [all, setAll] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("series") || "all");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/artworks").then((r) => setAll(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSearchParams(filter === "all" ? {} : { series: filter }, { replace: true });
  }, [filter, setSearchParams]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = filter === "all" ? all : all.filter((a) => a.collection === filter);
  const current = SERIES.find((s) => s.value === filter);

  return (
    <main data-testid="archive-page" className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-40 md:pt-52 pb-16">
      <Seo
        title={(SERIES_SEO[filter] || SERIES_SEO.all).seo_title}
        description={(SERIES_SEO[filter] || SERIES_SEO.all).seo_description}
        canonical={`${window.location.origin}/archive${filter !== "all" ? `?series=${filter}` : ""}`}
      />
      <header className="border-b hairline pb-8 mb-12">
        <div className="label mb-6">Official Archive</div>
        <h1 className="font-display font-800 uppercase leading-[0.9] tracking-[-0.02em] text-6xl md:text-8xl">Archive</h1>
        <p className="mt-8 text-[var(--muted)] max-w-xl">
          RCKT 001 — 1000. A permanent, numbered body of original works. Sold pieces remain in the record.
        </p>
      </header>

      <div className="flex items-center justify-between mb-14">
        <div className="label" data-testid="archive-count">{filtered.length} {filtered.length === 1 ? "work" : "works"}</div>
        <div className="relative" ref={ref}>
          <button
            data-testid="series-filter-toggle"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-3 font-display uppercase tracking-[0.08em] text-sm border hairline px-5 py-3 bg-white hover:border-[var(--ink)] transition-colors"
          >
            <span className="label">Series</span>
            <span>{current?.label}</span>
            <ChevronDown size={15} className="transition-transform duration-300" style={{ transform: open ? "rotate(180deg)" : "none" }} />
          </button>
          <AnimatePresence>
            {open && (
              <motion.ul
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="absolute right-0 mt-2 w-56 bg-white border hairline z-40 shadow-sm"
                data-testid="series-filter-menu"
              >
                {SERIES.map((s) => (
                  <li key={s.value}>
                    <button
                      data-testid={`series-option-${s.value}`}
                      onClick={() => { setFilter(s.value); setOpen(false); }}
                      className="w-full text-left px-5 py-3 font-display uppercase tracking-[0.06em] text-sm hover:bg-[var(--paper)] transition-colors"
                      style={{ opacity: filter === s.value ? 1 : 0.55 }}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={filter}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          {filtered.length === 0 ? (
            <div className="py-24 label" data-testid="archive-empty">No works in this series yet — <Link to="/archive" className="underline">view all</Link>.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {filtered.map((a, i) => (
                <ArtworkCard key={a.slug} art={a} index={i} />
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </main>
  );
}
